/** Saved-session review and time correction UI; Rust validates bounds and repairs learning evidence.
 * @module apps/web/src/components/activities/SavedActivitySessions
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { taskToUI, type SavedActivitySession, type TaskUI } from '@fluxability/types';
import { api } from '@/lib/api';
import { useUserStore } from '@/stores/userStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Inputs for the saved-session history within the activity dialog. */
interface Props {
  /** Activity whose owned history should be loaded. */
  task: TaskUI;
  /** Apply the server's refreshed activity after correction. */
  onCorrected: (task: TaskUI) => void;
}

/** Mount separate presentation state for each account and activity. */
export function SavedActivitySessions(props: Props) {
  const owner = useUserStore(state => state.userId);
  if (!owner || owner !== props.task.userId) return null;
  return <SessionHistory key={`${owner}:${props.task.id}`} {...props} />;
}

/** Display authoritative history and keep failed edits available for a safe retry. */
function SessionHistory({ task, onCorrected }: Props) {
  const [sessions, setSessions] = useState<SavedActivitySession[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [editing, setEditing] = useState<SavedActivitySession | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const live = useRef(false);
  const startInput = useRef<HTMLInputElement>(null);
  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  const returnFocus = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (editing) startInput.current?.focus();
    else if (returnFocus.current) {
      editButtons.current.get(returnFocus.current)?.focus();
      returnFocus.current = null;
    }
  }, [editing]);
  const retry = useRef<{ body: string; key: string } | null>(null);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    void api.training.getActivitySessions(task.id, task.userId).then(result => {
      if (!current || useUserStore.getState().userId !== task.userId) return;
      setLoading(false);
      if (!result.success || !result.data) {
        setError(result.error?.message || 'Could not load saved sessions.');
        return;
      }
      setSessions(result.data.sessions);
      setHasMore(result.data.hasMore);
    }).catch(() => {
      if (current) { setLoading(false); setError('Could not load saved sessions.'); }
    });
    return () => { current = false; };
  }, [task.id, task.userId, reload]);

  /** Begin a draft without changing the stored session. */
  function edit(session: SavedActivitySession) {
    returnFocus.current = session.id;
    setEditing(session);
    setStart(localInput(session.startedAt));
    setEnd(localInput(session.endedAt));
    setError('');
    setSaved('');
    retry.current = null;
  }

  /** Send explicit instants and retain the same retry key after an uncertain response. */
  async function save() {
    if (!editing || busy || useUserStore.getState().userId !== task.userId) return;
    const started = new Date(start);
    const ended = new Date(end);
    if (!Number.isFinite(started.getTime()) || !Number.isFinite(ended.getTime()) || ended <= started) {
      setError('Choose a finish time after the start time.');
      return;
    }
    const wallStart = start.length === 16 ? `${start}:00` : start;
    const wallEnd = end.length === 16 ? `${end}:00` : end;
    if (localInput(started.toISOString()) !== wallStart || localInput(ended.toISOString()) !== wallEnd) {
      setError('This local time does not exist because the clock changed. Choose a valid time.');
      return;
    }
    const payload = {
      expectedOwnerId: task.userId,
      startedAt: start === localInput(editing.startedAt) ? editing.startedAt : started.toISOString(),
      endedAt: end === localInput(editing.endedAt) ? editing.endedAt : ended.toISOString(),
      expectedRevision: editing.revision,
    };
    const body = JSON.stringify({ id: editing.id, ...payload });
    if (retry.current?.body !== body) retry.current = { body, key: crypto.randomUUID() };
    setBusy(true);
    setError('');
    try {
      const result = await api.training.correctActivitySession(editing.id, {
        ...payload, idempotencyKey: retry.current.key,
      });
      if (!live.current || useUserStore.getState().userId !== task.userId) return;
      if (!result.success || !result.data) {
        setError(result.error?.message || 'Could not correct this session. Your draft is retained.');
        return;
      }
      setSessions(rows => rows.map(row => row.id === editing.id ? result.data!.session : row));
      setEditing(null);
      setSaved('Session times corrected. Calendar and learning data updated.');
      window.dispatchEvent(new CustomEvent('fluxability-energy-evidence-changed', { detail: { ownerId: task.userId } }));
      onCorrected(taskToUI(result.data.task));
    } catch {
      if (live.current) setError('The result could not be confirmed. Retry this edit to check it safely.');
    } finally {
      if (live.current) setBusy(false);
    }
  }

  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <details className="rounded-md border p-3">
      <summary className="cursor-pointer text-sm font-medium">Saved sessions</summary>
      <div className="mt-3 space-y-3">
        <p className="text-xs text-muted-foreground">Times shown in {zone}. Correct an existing session here; logging below creates a new session.</p>
        {loading && <p role="status">Loading saved sessions…</p>}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {saved && <p role="status" className="text-sm">{saved}</p>}
        {!loading && sessions.length === 0 && !error && <p className="text-sm">No saved sessions yet.</p>}
        {sessions.map(session => (
          <article key={session.id} className="space-y-2 rounded border p-3">
            <p className="text-sm">{new Date(session.startedAt).toLocaleString()} – {new Date(session.endedAt).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{session.activeDurationMinutes} active minutes · {session.segments.length} work {session.segments.length === 1 ? 'segment' : 'segments'}</p>
            {session.notes && <p className="whitespace-pre-wrap break-words text-sm">{session.notes}</p>}
            {editing?.id === session.id ? (
              <div className="space-y-3">
                <Label className="block">Correct start
                  <Input ref={startInput} type="datetime-local" step="1" value={start} disabled={busy} onChange={event => setStart(event.target.value)} />
                </Label>
                <Label className="block">Correct finish
                  <Input type="datetime-local" step="1" value={end} disabled={busy} onChange={event => setEnd(event.target.value)} />
                </Label>
                <p className="text-xs text-muted-foreground">Resolved times: {resolvedTime(start === localInput(session.startedAt) ? session.startedAt : start)} to {resolvedTime(end === localInput(session.endedAt) ? session.endedAt : end)}.</p>
                <p className="text-xs text-muted-foreground">Known pauses stay excluded. Notes and completion stay unchanged.</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save corrected times'}</Button>
                  <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setEditing(null)}>Cancel edit</Button>
                </div>
              </div>
            ) : <Button ref={node => { if (node) editButtons.current.set(session.id, node); else editButtons.current.delete(session.id); }} type="button" size="sm" variant="outline" disabled={busy} onClick={() => edit(session)}>Edit session times</Button>}
          </article>
        ))}
        {hasMore && <p className="text-xs text-muted-foreground">Showing the latest 100 sessions.</p>}
        <Button type="button" size="sm" variant="ghost" disabled={busy || loading} onClick={() => { setEditing(null); setReload(value => value + 1); }}>Refresh saved sessions</Button>
      </div>
    </details>
  );
}

/** Format a saved instant for the browser's local date-time input, preserving seconds. */
function localInput(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 19);
}

/** Show the resolved offset so repeated local clock hours are explicit before saving. */
function resolvedTime(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString(undefined, { timeZoneName: 'longOffset' })
    : 'choose a valid time';
}
